import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { FiLock, FiMail, FiEye, FiEyeOff, FiX } from "react-icons/fi";
import "./AdminLogin.css";

const AdminLogin: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  
  // Get error, loading, and user from useAuth
  const { signIn, user, error: authError, loading } = useAuth();
  const navigate = useNavigate();

  // Redirect if user is already authenticated
  useEffect(() => {
    if (user) {
      navigate("/admin/dashboard"); // Adjust path as needed
    }
  }, [user, navigate]);

  // Clear local error when auth error changes
  useEffect(() => {
    if (authError) {
      setLocalError(null);
    }
  }, [authError]);

  const handleLogin = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!email || !password) {
      setLocalError("Please enter both email and password");
      return;
    }

    setLocalError(null);

    // Call signIn - errors will be handled by the useAuth hook
    await signIn(email, password);
    
    // No need to check result since signIn returns void
    // Errors are automatically set in the auth context
  };

  const handleEnterKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !loading) {
      e.preventDefault();
      e.stopPropagation();
      handleLogin();
    }
  };

  // Display either local validation error or auth error
  const displayError = localError || authError;

  return (
    <div className="admin-login-container">
      <div className="admin-login-card">
        {/* Header */}
        <div className="admin-login-header">
          <h1 className="admin-login-title">
            <FiLock style={{ marginRight: 6 }} /> Admin Portal
          </h1>
          <p className="admin-login-subtitle">
            Restricted access. Authorized personnel only.
          </p>
        </div>

        {/* Error box */}
        {displayError && (
          <div className="admin-login-error-box">
            <FiX style={{ marginRight: 6 }} />
            {displayError}
          </div>
        )}

        {/* Form */}
        <form className="admin-login-form" onSubmit={handleLogin}>
          {/* Email */}
          <div className="admin-login-field">
            <label htmlFor="email">Email</label>
            <div className="input-wrapper">
              <FiMail className="input-icon" />
              <input
                id="email"
                type="email"
                value={email}
                placeholder="Enter your email"
                autoComplete="username"
                disabled={loading}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={handleEnterKey}
              />
            </div>
          </div>

          {/* Password */}
          <div className="admin-login-field">
            <label htmlFor="password">Password</label>
            <div className="input-wrapper">
              <FiLock className="input-icon" />
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                placeholder="Enter your password"
                autoComplete="current-password"
                disabled={loading}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={handleEnterKey}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword((s) => !s)}
                disabled={loading}
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            className="admin-login-button"
            disabled={loading || !email || !password}
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>

        {/* Footer */}
        <div className="admin-login-footer">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="admin-login-back-button"
            disabled={loading}
          >
            ← Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;